import { Box, Stack } from "@mui/material";
import { ReactNode, useEffect, useState } from "react";
import { useLangContext } from "../../../context/langContext";
import { dateFormatter } from "../../../utils/date-formatter";
import CustomTable from "../../common/tables/Table";
import { useProtectedAxiosInstance } from "../../../hooks/axiosInstance";
import { useNotificationContext } from "../../../context/notificationContext";
import errorResolver from "../../../utils/error-resolver";
import MuiTabPanel from "../../common/tab-panel/TabPanel";
import { useWorkspaceContext } from "../../../context/workspaceContext";
import { useUrl } from "../../../hooks/urlEncoding";
import { Link, useLocation } from "react-router-dom";
import { ITableSchema } from "../../../utils/types";
import useTableSchema from "../../../hooks/useTableSchema";
import {
  RecordCallOption,
  SecureCallStatus,
} from "../../../services/Schemas/SecureCalls";
import {
  SecureCallColumnSortable,
  SecureCallsListRequest,
} from "../../../services/Endpoints/secure-calls/SecureCallsListEndpoints";
import {
  FilterOperatorEnum,
  SortDirectionEnum,
} from "../../../services/Schemas/ListQueryString";
import NumberFilter from "../../common/tables/filters/NumberFilter";
import TextFilter from "../../common/tables/filters/TextFilter";
import DateTimeFilter from "../../common/tables/filters/DateTimeFilter";
import SelectionFilter from "../../common/tables/filters/SelectionFilter";
import { routerPaths } from "../../../constants/Paths";
import UserLink from "../common/UserLink";
import { useCanAccess } from "../../../hooks/canAccess";
import { AccessEnums } from "../../../constants/accessEnum";
import { WorkspaceListColumnFilterable } from "../../../services/Endpoints/workspaces";
import AudioPlayer from "../../common/audio-player/AudioPlayer";
import { AudioPlayerProvider } from "../../../context/audioPlayerContext";
import useUrlSort from "../../../hooks/useUrlSort";

export interface ISecureCall {
  id: number;
  first_side_number: string;
  second_side_number: string;
  created_at: string;
  status: ReactNode | string;
  actual_duration: number;
  actual_cost: number;
  workspace_id: ReactNode | number;
}

const secureCallStatus = {
  [SecureCallStatus.CREATED]: "ایجاد شده",
  [SecureCallStatus.WAITING_FOR_SEND]: "در انتظار ارسال",
  [SecureCallStatus.SENDING]: "در حال ارسال",
  [SecureCallStatus.BLOCKED]: "مسدود شده",
  [SecureCallStatus.FAILED]: "ناموفق",
  [SecureCallStatus.ANSWERED]: "پاسخ داده شده",
  [SecureCallStatus.BUSY]: "اشغال",
  [SecureCallStatus.REJECTED]: "رد شده",
  [SecureCallStatus.NO_ANSWER]: "بی‌پاسخ",
  [SecureCallStatus.DEVICE_OFF]: "دستگاه خاموش است",
  [SecureCallStatus.INVALID_NUMBER]: "شماره نامعتبر",
  [SecureCallStatus.UNREACHABLE]: "غیرقابل دسترسی",
  [SecureCallStatus.CANCELED]: "لغو شده",
};

const recordCall = {
  [RecordCallOption.YES]: "بله",
  [RecordCallOption.NO]: "خیر",
};

export interface ISupportTabsComponent {
  setHasSecureCall: (payload: boolean) => void;
}

export default function SecureCallsTable() {
  const { parseQueryString, queryStringUrl, setParamWithSearchParams } =
    useUrl();
  const parsed = parseQueryString();
  const initialPage = parsed.page ? Number(parsed.page) : 1;
  const initialRows = parsed.count ? Number(parsed.count) : 10;
  const { lang } = useLangContext();
  const [page, setPage] = useState(initialPage);
  const [rowsPerPage, setRowsPerPage] = useState(initialRows);
  const [data, setData] = useState<ISecureCall[] | []>([]);
  const [total, setTotal] = useState(0);
  const { workspace } = useWorkspaceContext();
  const [waiting, setWaiting] = useState(true);
  const [callsReload, setCallsReload] = useState(false);
  const location = useLocation();
  const protectedInstance = useProtectedAxiosInstance();
  const { setNotification } = useNotificationContext();
  const { can } = useCanAccess();
  const { parseUrlSorts, applyUrlSorts } = useUrlSort();
  const sorts = parseUrlSorts();

  const getData = async () => {
    try {
      setWaiting(true);
      const secureCallListRequest = new SecureCallsListRequest(
        protectedInstance
      );
      const resp = await secureCallListRequest.send({
        page,
        count: rowsPerPage,
        ...parseQueryString(),
      });
      setTotal(resp.data.total);
      const secureCalls = resp.data.secure_calls.map(
        ({
          id,
          workspace_id,
          first_side_number,
          second_side_number,
          created_at,
          status,
          actual_duration,
          actual_cost,
          recording_file,
        }) => ({
          id,
          first_side_number,
          second_side_number,
          created_at,
          workspace_id: (
            <UserLink
              disabled={!can(AccessEnums.AdminWorkspacesList)}
              user={{ id: workspace_id }}
              link={queryStringUrl(routerPaths.WORKSPACES, {
                filters: {
                  [WorkspaceListColumnFilterable.ID]: {
                    column: WorkspaceListColumnFilterable.ID,
                    value: workspace_id,
                    operator: FilterOperatorEnum.EQUAL,
                  },
                },
              })}
            />
          ),
          status: secureCallStatus[status],
          actual_duration,
          actual_cost,
          operations: (
            <Stack justifyContent="center" alignItems="center" direction="row">
              {recording_file && (
                <AudioPlayer
                  src={recording_file.url}
                  name={recording_file.original_name}
                />
              )}
            </Stack>
          ),
        })
      );
      setData(secureCalls);
      setWaiting(false);
    } catch (error) {
      errorResolver({ error, setNotification });
      setWaiting(false);
      console.error(error);
    }
  };

  useEffect(() => {
    getData();
  }, [workspace, callsReload, page, rowsPerPage, location.search]);

  const _columns: ITableSchema[] = [
    {
      key: "id",
      label: "شناسه",
      minWidth: 150,
      align: "center",
      FilterComponent: NumberFilter,
      sortable: true,
    },
    {
      key: "first_side_number",
      label: "طرف اول",
      minWidth: 150,
      align: "center",
      FilterComponent: TextFilter,
    },
    {
      key: "second_side_number",
      label: "طرف دوم",
      minWidth: 150,
      align: "center",
      FilterComponent: TextFilter,
    },
    {
      key: "workspace_id",
      label: "شناسه تخته",
      minWidth: 150,
      align: "center",
      FilterComponent: NumberFilter,
    },
    {
      key: "created_at",
      label: "زمان ایجاد",
      minWidth: 150,
      align: "center",
      FilterComponent: DateTimeFilter,
      format: (value: string) => value && dateFormatter({ lang, date: value }),
    },
    {
      key: "status",
      label: "وضعیت تماس",
      minWidth: 120,
      align: "center",
      FilterComponent: SelectionFilter,
      options: secureCallStatus,
    },
    {
      key: "actual_duration",
      label: "طول مکالمه",
      minWidth: 120,
      align: "center",
      FilterComponent: NumberFilter,
    },
    {
      key: "actual_cost",
      label: "هزینه‌ تماس",
      minWidth: 120,
      align: "center",
      FilterComponent: NumberFilter,
    },
    {
      key: "operations",
      label: "پلی کردن صوت",
      minWidth: 500,
      align: "center",
    },
  ];
  const { filtersSchema, columns } = useTableSchema(_columns);

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
    setParamWithSearchParams("page", String(newPage));
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const newRows = +event.target.value;
    setRowsPerPage(newRows);
    setParamWithSearchParams("count", String(newRows));
  };

  return (
    <AudioPlayerProvider>
      <Box
        sx={{ width: "100%", typography: "body1", backgroundColor: "white" }}
      >
        <CustomTable
          reloadAction={() => setCallsReload(!callsReload)}
          hasWaiting={waiting}
          hasSearchFilter
          hasBottomPagination
          hasTopPagination
          columns={columns}
          count={total}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
          page={page}
          rows={data}
          rowsPerPage={rowsPerPage}
          filtersSchema={filtersSchema}
          onApplySorts={applyUrlSorts}
          sorts={sorts}
        />
      </Box>
    </AudioPlayerProvider>
  );
}
